import { TEXT_ANNOTATION_TYPE, type TextAnnotationType } from 'src/data/annotation';
import {
  ASSET_TYPE,
  type AssetType,
  getAssetTypeMaxCopyLength,
  isAssetSupportSuggestKeywords,
  isCanvasAssetType,
  isLandingAssetType,
  isSmsAssetType,
  LANDING_ASSET_TYPE,
} from 'src/data/asset';
import { isExtractingBriefType } from 'src/data/brief';
import { BLOCKED_REASON, type BlockedReason, textIsTooLongForScoring } from 'src/data/common';
import {
  AI_GENERATION_MODE,
  AI_RECOMPOSE_MODE,
  AI_SUGGESTION_MODE,
  type AiGenerationMode,
  type AiSuggestionMode,
  GENERATION_GUIDANCE_TYPE,
  type GenerationGuidanceType,
  type SuggestionOrRecomposeMode,
} from 'src/data/generation';
import {
  doesToolSupportCustomerPersona,
  doesToolSupportSuggestKeywords,
  GENERATION_TOOL,
  type GenerationTool,
  getToolBriefType,
  getToolDefaultAssetType,
  isBasicContentImproverTool,
} from 'src/data/generationTool';
import { PROJECT_TYPE, type ProjectType } from 'src/data/project';
import { VARIATION_SOURCE, type VariationSource } from 'src/data/variation';

export const generationModeFromVariationSource = (
  source: VariationSource
): AiGenerationMode | undefined => {
  switch (source) {
    case VARIATION_SOURCE.suggest:
    case VARIATION_SOURCE.defaultSuggest:
      return AI_GENERATION_MODE.suggest;

    case VARIATION_SOURCE.recompose:
      return AI_GENERATION_MODE.recompose;

    default:
      return undefined;
  }
};

export const areSuggestKeywordsDisabled = ({
  projectType,
  suggestionMode,
  assetType,
}: {
  projectType: ProjectType;
  suggestionMode: AiSuggestionMode;
  assetType: AssetType;
}): boolean => {
  // in case of Landing page builder project type
  if (
    projectType === PROJECT_TYPE.landingPageBuilder &&
    assetType !== ASSET_TYPE.landingBuilderCta
  ) {
    return false;
  }

  if (isCanvasAssetType(assetType)) {
    return !isAssetSupportSuggestKeywords(assetType);
  }

  if (isLandingAssetType(assetType)) {
    return (
      [AI_SUGGESTION_MODE.standard, AI_SUGGESTION_MODE.rewrite] as AiSuggestionMode[]
    ).includes(suggestionMode);
  }

  // Keywords are disabled in "quote" and "list" modes
  if (
    ([AI_SUGGESTION_MODE.quotes, AI_SUGGESTION_MODE.list] as AiSuggestionMode[]).includes(
      suggestionMode
    )
  ) {
    return true;
  }

  // Allow to use "keywords" in both google asset types in all "suggestion modes"
  if (
    ([ASSET_TYPE.googleHeadline, ASSET_TYPE.googleDescription] as AssetType[]).includes(assetType)
  ) {
    return false;
  }

  return false;
};

// TODO: compare to suggestion modes on production
export const doesToolSupportSuggestKeywordsForMode = ({
  generationTool,
  suggestionMode,
}: {
  generationTool: GenerationTool;
  suggestionMode: SuggestionOrRecomposeMode;
}): boolean => {
  if (!doesToolSupportSuggestKeywords(generationTool)) {
    return false;
  }

  const assetType = getToolDefaultAssetType(generationTool);
  if (assetType && isLandingAssetType(assetType)) {
    return (
      (
        [
          LANDING_ASSET_TYPE.landingBuilderSubHeadline,
          LANDING_ASSET_TYPE.features,
          LANDING_ASSET_TYPE.benefits,
          ASSET_TYPE.landingBuilderHeadline,
        ] as AssetType[]
      ).includes(assetType) ||
      suggestionMode === AI_SUGGESTION_MODE.creative ||
      suggestionMode === AI_RECOMPOSE_MODE.relaxed
    );
  }

  // Keywords are disabled in "quote" modes
  if (suggestionMode === AI_SUGGESTION_MODE.quotes) {
    return false;
  }

  return true;
};

export const doesToolSupportsPersonaForMode = ({
  generationTool,
  suggestionMode,
}: {
  generationTool: GenerationTool;
  suggestionMode: SuggestionOrRecomposeMode;
}): boolean => {
  if (!doesToolSupportCustomerPersona(generationTool)) {
    return false;
  }

  if (generationTool === GENERATION_TOOL.facebookPrimaryTextContent) {
    return suggestionMode !== AI_SUGGESTION_MODE.quotes;
  }

  if (isBasicContentImproverTool(generationTool)) {
    return suggestionMode === AI_RECOMPOSE_MODE.relaxed;
  }

  if (suggestionMode === AI_SUGGESTION_MODE.quotes) {
    return false;
  }

  return true;
};

const getSmsAssetMaxCopyLength = (suggestionMode: AiSuggestionMode): number => {
  switch (suggestionMode) {
    case AI_SUGGESTION_MODE.sms160:
      return 160;

    case AI_SUGGESTION_MODE.sms320:
      return 320;

    case AI_SUGGESTION_MODE.sms480:
    default:
      return 480;
  }
};
export const getAssetMaxCopyLength = ({
  assetType,
  suggestionMode,
}: {
  assetType: AssetType;
  suggestionMode: AiSuggestionMode;
}): number | undefined => {
  if (isSmsAssetType(assetType)) {
    return getSmsAssetMaxCopyLength(suggestionMode);
  }
  return getAssetTypeMaxCopyLength(assetType);
};

const ANNOTATION_TYPE_TO_GUIDANCE_TYPE: Record<string, GenerationGuidanceType> = {
  [TEXT_ANNOTATION_TYPE.benefits]: GENERATION_GUIDANCE_TYPE.benefit,
  [TEXT_ANNOTATION_TYPE.pains]: GENERATION_GUIDANCE_TYPE.pain,
  [TEXT_ANNOTATION_TYPE.features]: GENERATION_GUIDANCE_TYPE.feature,
  [TEXT_ANNOTATION_TYPE.campaignKeywords]: GENERATION_GUIDANCE_TYPE.campaignKeyword,
};
export const guidanceTypeFromAnnotationType = (
  annotationType: TextAnnotationType
): GenerationGuidanceType => {
  return ANNOTATION_TYPE_TO_GUIDANCE_TYPE[annotationType] || null;
};
export const annotationTypeFromGuidanceType = (
  guidanceType: GenerationGuidanceType
): TextAnnotationType | undefined => {
  return Object.entries(ANNOTATION_TYPE_TO_GUIDANCE_TYPE).find(
    ([, value]) => value === guidanceType
  )?.[0] as TextAnnotationType;
};

export const generationToolSupportsExtraction = (tool: GenerationTool): boolean => {
  const briefType = getToolBriefType(tool);
  return briefType ? isExtractingBriefType(briefType) : false;
};

export const calculateBlockedReason = (text: string): BlockedReason | undefined => {
  if (text && textIsTooLongForScoring(text)) {
    return BLOCKED_REASON.maxWordsExceeded;
  }
  return undefined;
};

export const alignSummarizerSuggestionModeForGeneration = ({
  assetType,
  suggestionMode,
}: {
  assetType: AssetType;
  suggestionMode: AiSuggestionMode;
}): AiSuggestionMode => {
  if (assetType === ASSET_TYPE.freeTextTopic) {
    return AI_SUGGESTION_MODE.topic;
  }
  return suggestionMode;
};

const SERVICE_GENERATION_MODES: SuggestionOrRecomposeMode[] = [AI_SUGGESTION_MODE.guided];
export const withoutServiceGenerationModes = (
  modes: SuggestionOrRecomposeMode[]
): SuggestionOrRecomposeMode[] => {
  return modes.filter((mode) => !SERVICE_GENERATION_MODES.includes(mode));
};
